import React, { useEffect, useMemo, useState } from 'react';
import { Tab } from '@headlessui/react';
import Modal from '../../../../../components/modals/Modal';
import { Button, Pagination } from '../../../../../components/common';
import CustomCheckbox from '../../../../../components/common/CustomCheckBox';
import { Input } from '../../../../../components/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useToast from '../../../../../state/hooks/useToast';
import plansService from '../../../../../services/api/smartbill/plansService';
import schoolService from '../../../../../services/api/unified-api/schoolService';
import { ListLoader } from '../../../../../components/loaders';
import { ErrorLoading, ListNoData } from '../../../../../components/common';
import { paginationInfo } from '../../../../../utils/pagination';

interface ModalAddToPlanProps {
  planId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void; // Called after either tab successfully adds
}

type Paged<T> = {
  list: T[];
  loading: boolean;
  loadingError: boolean;
  pager: { pageSize: number; page: number; totalCount: number };
};

const ModalAddToPlan: React.FC<ModalAddToPlanProps> = ({ planId, open, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const handleClose = () => {
    setTabKey(tabKey + 1);
    onClose();
  };

  const [tabKey, setTabKey] = useState(0);

  // Schools Tab State
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolPage, setSchoolPage] = useState(1);
  const [schoolsData, setSchoolsData] = useState<Paged<any>>({
    list: [],
    loading: false,
    loadingError: false,
    pager: { pageSize: 10, page: 1, totalCount: 0 },
  });
  const [selectingAllSchools, setSelectingAllSchools] = useState(false);

  const planSchoolsQuery = useQuery({
    queryKey: ['plan-schools', planId],
    queryFn: () => plansService.getPlanSchools(planId),
    enabled: open,
  });

  const fetchSchools = async (page: number, searchName?: string) => {
    setSchoolsData((prev: any) => ({ ...prev, loading: true, loadingError: false }));
    try {
      const filters: any = {};
      if (searchName) filters.name = searchName;
      const result = await schoolService.getSchools({
        page,
        pageSize: 10,
        direction: 'asc',
        properties: 'name',
        ...filters,
      });
      if (result.success) {
        setSchoolsData({
          list: result.data || [],
          loading: false,
          loadingError: false,
          pager: result.pager || { pageSize: 10, page, totalCount: 0 },
        });
      } else {
        setSchoolsData((prev: any) => ({ ...prev, loading: false, loadingError: true }));
      }
    } catch (e) {
      setSchoolsData((prev: any) => ({ ...prev, loading: false, loadingError: true }));
    }
  };

  useEffect(() => {
    if (open) {
      fetchSchools(1, '');
      setSelectedSchoolIds([]);
    }
  }, [open]);

  // Show the raw list returned by API without filtering out already-added schools
  const allSchools = schoolsData.list;
  const schoolTotalPages = Math.ceil((schoolsData.pager.totalCount || 0) / (schoolsData.pager.pageSize || 10));

  const addSchoolsMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      return plansService.createSchoolPlans(planId, ids);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-schools', planId] });
      addToast({ type: 'success', text: `${selectedSchoolIds.length} school(s) added successfully` });
      onSuccess();
      handleClose();
    },
    onError: (error: any) => {
      addToast({ type: 'error', text: error?.response?.data?.message || 'Failed to add schools' });
    },
  });

  // Features Tab State
  const [selectedFeatureCodes, setSelectedFeatureCodes] = useState<string[]>([]);
  const [featureSearch, setFeatureSearch] = useState('');
  const [featurePage, setFeaturePage] = useState(1);
  const itemsPerPage = 10;
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualData, setManualData] = useState({ displayName: '', description: '', systemCode: '' });

  const planFeaturesQuery = useQuery({
    queryKey: ['plan-features', planId],
    queryFn: () => plansService.getPlanFeatures(planId),
    enabled: open,
  });

  const allFeaturesQuery = useQuery({
    queryKey: ['all-master-features'],
    queryFn: () => plansService.getAllFeatures(),
    enabled: open,
  });

  const globalPlanFeaturesQuery = useQuery({
    queryKey: ['plan-features-all'],
    queryFn: () => plansService.getPlanFeatures(),
    enabled: open,
  });

  const featuresLoading = (allFeaturesQuery.isLoading && !allFeaturesQuery.data) && (globalPlanFeaturesQuery.isLoading && !globalPlanFeaturesQuery.data);
  const featuresError = (allFeaturesQuery.isError && !allFeaturesQuery.data) && (globalPlanFeaturesQuery.isError && !globalPlanFeaturesQuery.data);

  const globalSystemCodes = useMemo(() => {
    const list = globalPlanFeaturesQuery.data?.data || [];
    return (list as any[]).map((f: any) => (f.systemCode || '').toLowerCase()).filter(Boolean);
  }, [globalPlanFeaturesQuery.data]);

  const manualSystemCode = manualData.systemCode.trim();
  const systemCodeTaken = manualSystemCode ? globalSystemCodes.includes(manualSystemCode.toLowerCase()) : false;

  const existingFeatureSystemCodes = useMemo(
    () => (planFeaturesQuery.data?.data || []).map((pf: any) => pf.systemCode),
    [planFeaturesQuery.data]
  );

  const normalizedMasterFeatures = useMemo(() => {
    const paymentsFeatures: any[] = allFeaturesQuery.data?.data || [];
    const planFeaturesAll: any[] = globalPlanFeaturesQuery.data?.data || [];
    const source: any[] = paymentsFeatures && paymentsFeatures.length > 0 ? paymentsFeatures : planFeaturesAll;

    const normalized = source.map((f: any) => ({
      id: f.id,
      displayName: f.displayName || f.name || f.systemCode || f.code,
      description: f.description || '',
      systemCode: f.systemCode || f.code,
    }));

    const seen = new Set<string>();
    return normalized.filter((item: any) => {
      const key = (item.systemCode || '').toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allFeaturesQuery.data, globalPlanFeaturesQuery.data]);

  const availableFeatures = useMemo(() => {
    let filtered = (normalizedMasterFeatures || []).filter(
      (feature: any) => !existingFeatureSystemCodes.includes(feature.systemCode)
    );
    if (featureSearch.trim()) {
      const q = featureSearch.toLowerCase();
      filtered = filtered.filter(
        (f: any) => f.displayName?.toLowerCase().includes(q) || f.systemCode?.toLowerCase().includes(q) || f.description?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [normalizedMasterFeatures, existingFeatureSystemCodes, featureSearch]);

  const featureTotalPages = Math.ceil(availableFeatures.length / itemsPerPage);
  const pagedFeatures = useMemo(() => {
    const start = (featurePage - 1) * itemsPerPage;
    return availableFeatures.slice(start, start + itemsPerPage);
  }, [availableFeatures, featurePage]);

  const addFeaturesMutation = useMutation({
    mutationFn: async ({ payloads }: { payloads: Array<{ displayName: string; description?: string; systemCode: string; planId: string; planIdAsUUID: string }> }) => {
      return plansService.createPlanFeaturesBulk(payloads);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-features', planId] });
      addToast({ type: 'success', text: 'Feature(s) added successfully' });
      onSuccess();
      handleClose();
    },
    onError: (error: any) => {
      addToast({ type: 'error', text: error?.response?.data?.message || 'Failed to add features' });
    },
  });

  const submitSchools = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSchoolIds.length === 0) {
      addToast({ type: 'error', text: 'Please select at least one school' });
      return;
    }
    addSchoolsMutation.mutate(selectedSchoolIds);
  };

  const submitFeatures = (e: React.FormEvent) => {
    e.preventDefault();

    if (manualSystemCode && systemCodeTaken) {
      addToast({ type: 'error', text: 'System code already exists. Choose a unique code or select from list.' });
      return;
    }

    const payloads: Array<{ displayName: string; description?: string; systemCode: string; planId: string; planIdAsUUID: string }> = [];

    // From selected feature checkboxes
    for (const code of selectedFeatureCodes) {
      const f = (normalizedMasterFeatures || []).find((x: any) => x.systemCode === code);
      if (f) {
        payloads.push({
          displayName: f.displayName || f.systemCode,
          description: f.description || '',
          systemCode: f.systemCode,
          planId,
          planIdAsUUID: planId,
        });
      }
    }

    // Manual entry if provided
    if (showManualForm && manualData.displayName.trim() && manualData.systemCode.trim()) {
      payloads.push({
        displayName: manualData.displayName.trim(),
        description: manualData.description.trim(),
        systemCode: manualData.systemCode.trim(),
        planId,
        planIdAsUUID: planId,
      });
    }

    if (payloads.length === 0) {
      addToast({ type: 'error', text: 'Select at least one feature or open manual form to add one' });
      return;
    }

    addFeaturesMutation.mutate({ payloads });
  };

  // Reset pages on search change
  useEffect(() => setSchoolPage(1), [schoolSearch]);
  useEffect(() => setFeaturePage(1), [featureSearch]);

  // Selection helpers for schools
  const toggleSchool = (id: number) => {
    setSelectedSchoolIds((prev: number[]) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const currentPageSchoolIds = allSchools.map((s: any) => s.id);
  const allSchoolPageSelected = currentPageSchoolIds.length > 0 && currentPageSchoolIds.every((id: number) => selectedSchoolIds.includes(id));
  const toggleAllCurrentSchoolPage = () => {
    setSelectedSchoolIds((prev: number[]) => {
      if (allSchoolPageSelected) {
        return prev.filter((id) => !currentPageSchoolIds.includes(id));
      }
      const merged = new Set<number>([...prev, ...currentPageSchoolIds]);
      return Array.from(merged);
    });
  };
  const selectAllCurrentSchoolPage = () => {
    setSelectedSchoolIds((prev: number[]) => Array.from(new Set<number>([...prev, ...currentPageSchoolIds])));
  };
  const clearCurrentSchoolPageSelection = () => {
    setSelectedSchoolIds((prev: number[]) => prev.filter((id) => !currentPageSchoolIds.includes(id)));
  };

  // Select all across the full result set
  const selectAllSchoolsAcrossResults = async () => {
    setSelectingAllSchools(true);
    try {
      const total = schoolsData.pager.totalCount || 0;
      // Request all items in one shot to gather IDs
      const filters: any = {};
      if (schoolSearch.trim()) filters.name = schoolSearch.trim();
      const result = await schoolService.getSchools({
        page: 1,
        pageSize: Math.max(total, 1),
        direction: 'asc',
        properties: 'name',
        ...filters,
      });
      if (result.success) {
        const ids: number[] = (result.data || [])
          .map((s: any) => s.id)
          .filter((id: any) => typeof id === 'number');
        setSelectedSchoolIds((prev) => Array.from(new Set<number>([...prev, ...ids])));
      }
    } finally {
      setSelectingAllSchools(false);
    }
  };

  // Selection helpers for features (by systemCode)
  const toggleFeature = (code: string) => {
    setSelectedFeatureCodes((prev: string[]) => (prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]));
  };
  const currentPageFeatureCodes = pagedFeatures.map((f: any) => f.systemCode);
  const allFeaturePageSelected = currentPageFeatureCodes.length > 0 && currentPageFeatureCodes.every((code: string) => selectedFeatureCodes.includes(code));
  const toggleAllCurrentFeaturePage = () => {
    setSelectedFeatureCodes((prev: string[]) => {
      if (allFeaturePageSelected) {
        return prev.filter((code) => !currentPageFeatureCodes.includes(code));
      }
      const merged = new Set<string>([...prev, ...currentPageFeatureCodes]);
      return Array.from(merged);
    });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div key={tabKey} className="w-[95vw] max-w-6xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-lg p-6 overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add Schools / Features to Plan</h3>

        <Tab.Group>
          <Tab.List className="flex gap-2 border-b dark:border-gray-700 mb-4">
            <Tab className={({ selected }) => `px-4 py-2 rounded-t-md text-sm ${selected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Schools</Tab>
            <Tab className={({ selected }) => `px-4 py-2 rounded-t-md text-sm ${selected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Features</Tab>
          </Tab.List>

          <Tab.Panels>
            {/* Schools Panel */}
            <Tab.Panel>
              <form onSubmit={submitSchools} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search Schools</label>
                  <div className="flex gap-2 mb-3">
                    <Input type="text" value={schoolSearch} onChange={(e) => setSchoolSearch(e.target.value)} placeholder="Search by name..." className="flex-1" />
                    <Button type="button" onClick={() => fetchSchools(1, schoolSearch.trim())} disabled={schoolsData.loading} className="px-4 py-2">
                      {schoolsData.loading ? 'Searching...' : 'Search'}
                    </Button>
                  </div>

                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Select schools to add</p>
                    <div className="flex gap-2">
                      <Button type="button" onClick={selectAllSchoolsAcrossResults} loading={selectingAllSchools} className="px-3 py-1 text-sm">{selectingAllSchools ? 'Selecting...' : `Select All (${schoolsData.pager.totalCount})`}</Button>
                      <Button type="button" onClick={() => setSelectedSchoolIds([])} className="px-3 py-1 text-sm">Clear All</Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Currently selected: {selectedSchoolIds.length}</p>

                  {schoolsData.loading ? (
                    <ListLoader loadingText='Loading Schools' className='px-8 py-24' />
                  ) : schoolsData.loadingError ? (
                    <ErrorLoading title='Error Loading Schools' message='There was a problem loading schools' className='px-8 py-24' onTryAgain={() => fetchSchools(schoolPage, schoolSearch.trim())} />
                  ) : allSchools.length === 0 ? (
                    <ListNoData title='No School Found' description={schoolSearch ? 'No schools match your search' : 'There are no schools to display'} className='px-8 py-24' />
                  ) : (
                    <>
                      <div className="max-w-full overflow-x-auto mb-4">
                        <table className="border-collapse table-auto w-full">
                          <thead>
                            <tr className="text-left border-b-2 dark:border-gray-700">
                              <th className="py-3 px-4 w-10">
                                <CustomCheckbox
                                  ariaLabel="Select all on page"
                                  checked={allSchoolPageSelected}
                                  onChange={toggleAllCurrentSchoolPage}
                                  size="md"
                                />
                              </th>
                              <th className="py-3 px-4">School</th>
                              <th className="py-3 px-4">Alias</th>
                              <th className="py-3 px-4">Location</th>
                              <th className="py-3 px-4">Phone No.</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y dark:divide-gray-700/60">
                            {allSchools.map((school: any) => {
                              const checked = selectedSchoolIds.includes(school.id);
                              return (
                                <tr key={school.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                  <td className="py-2 px-4">
                                    <CustomCheckbox
                                      ariaLabel={`Select school ${school.name}`}
                                      checked={checked}
                                      onChange={() => toggleSchool(school.id)}
                                      size="md"
                                    />
                                  </td>
                                  <td className="py-2 px-4">{school.name}</td>
                                  <td className="py-2 px-4">{school.alias}</td>
                                  <td className="py-2 px-4">{school.location}</td>
                                  <td className="py-2 px-4">{school.contact || school.contactPhone || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className='flex justify-between items-center flex-wrap'>
                        <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>{paginationInfo(schoolsData.pager.pageSize, schoolsData.pager.totalCount, schoolPage)}</p>
                        <Pagination
                          currentPage={schoolPage}
                          pageSize={schoolsData.pager.pageSize}
                          totalCount={schoolsData.pager.totalCount}
                          onPageChange={(page) => {
                            setSchoolPage(page);
                            fetchSchools(page, schoolSearch.trim());
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" onClick={handleClose} className="!text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:!text-gray-300 dark:hover:bg-gray-600">Cancel</Button>
                  <Button type="submit" loading={(addSchoolsMutation as any).isPending} disabled={selectedSchoolIds.length === 0 || (addSchoolsMutation as any).isPending}>
                    {(addSchoolsMutation as any).isPending ? 'Adding...' : `Add ${selectedSchoolIds.length} School${selectedSchoolIds.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </form>
            </Tab.Panel>

            {/* Features Panel */}
            <Tab.Panel>
              <form onSubmit={submitFeatures} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search Features</label>
                  <div className="mb-3">
                    <Input type="text" value={featureSearch} onChange={(e) => setFeatureSearch(e.target.value)} placeholder="Search features by name, code, or description..." className="w-full" />
                  </div>

                  {featuresLoading ? (
                    <ListLoader loadingText='Loading Features' className='px-8 py-24' />
                  ) : featuresError ? (
                    <ErrorLoading title='Error Loading Features' message='There was a problem loading features' className='px-8 py-24' onTryAgain={() => { allFeaturesQuery.refetch(); globalPlanFeaturesQuery.refetch(); }} />
                  ) : availableFeatures.length === 0 ? (
                    <ListNoData title='No Feature Found' description={featureSearch ? 'No features match your search' : 'No feature available to add'} className='px-8 py-24' />
                  ) : (
                    <>
                      <div className="max-w-full overflow-x-auto mb-4">
                        <table className="border-collapse table-auto w-full">
                          <thead>
                            <tr className="text-left border-b-2 dark:border-gray-700">
                              <th className="py-3 px-4 w-10">
                                <CustomCheckbox
                                  ariaLabel="Select all features on page"
                                  checked={allFeaturePageSelected}
                                  onChange={toggleAllCurrentFeaturePage}
                                  size="sm"
                                />
                              </th>
                              <th className="py-3 px-4">Name</th>
                              <th className="py-3 px-4">Code</th>
                              <th className="py-3 px-4">Description</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y dark:divide-gray-700/60">
                            {pagedFeatures.map((f: any) => {
                              const checked = selectedFeatureCodes.includes(f.systemCode);
                              return (
                                <tr key={f.systemCode} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                  <td className="py-2 px-4">
                                    <CustomCheckbox
                                      ariaLabel={`Select feature ${f.systemCode}`}
                                      checked={checked}
                                      onChange={() => toggleFeature(f.systemCode)}
                                      size="sm"
                                    />
                                  </td>
                                  <td className="py-2 px-4">{f.displayName || f.systemCode}</td>
                                  <td className="py-2 px-4">{f.systemCode}</td>
                                  <td className="py-2 px-4">{f.description}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className='flex justify-between items-center flex-wrap'>
                        <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>{paginationInfo(itemsPerPage, availableFeatures.length, featurePage)}</p>
                        <Pagination
                          currentPage={featurePage}
                          pageSize={itemsPerPage}
                          totalCount={availableFeatures.length}
                          onPageChange={(page) => setFeaturePage(page)}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Add a new feature manually</p>
                    <Button type="button" onClick={() => setShowManualForm(!showManualForm)} className="text-sm px-3 py-1">{showManualForm ? 'Hide' : 'Show'} Manual Form</Button>
                  </div>

                  {showManualForm && (
                    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Display Name</label>
                          <Input type="text" value={manualData.displayName} onChange={(e) => setManualData((prev: any) => ({ ...prev, displayName: e.target.value }))} placeholder="Enter display name" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">System Code</label>
                            {manualSystemCode && systemCodeTaken && <span className="text-xs text-red-600">Already exists globally</span>}
                          </div>
                          <Input type="text" value={manualData.systemCode} onChange={(e) => setManualData((prev: any) => ({ ...prev, systemCode: e.target.value }))} placeholder="Unique code, e.g. ATTENDANCE" />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                          <Input type="text" value={manualData.description} onChange={(e) => setManualData((prev: any) => ({ ...prev, description: e.target.value }))} placeholder="Short description" />
                          <p className="mt-1 text-xs text-gray-500">Provide a clear name and unique system code. Description is optional.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" onClick={handleClose} className="!text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:!text-gray-300 dark:hover:bg-gray-600">Cancel</Button>
                  <Button type="submit" loading={(addFeaturesMutation as any).isPending} disabled={(selectedFeatureCodes.length === 0 && (!showManualForm || !manualData.displayName.trim() || !manualData.systemCode.trim())) || (addFeaturesMutation as any).isPending}>
                    {(addFeaturesMutation as any).isPending ? 'Adding...' : 'Add Feature(s)'}
                  </Button>
                </div>
              </form>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </Modal>
  );
};

export default ModalAddToPlan;
